<svelte:options />

<script lang="ts">
  import { isSidebarOpen, selectedFeature, Selection } from './sidebar-service';
  import type { A11yLabels, Feature } from './sidebar.types';

  export let feature: Feature;
  export let a11yLabels: A11yLabels;

  $: updateSelection(feature, $selectedFeature);
  $: updatA11yLabel(a11yLabels, feature);

  let isSelected = false;
  let ariaSuffix: string;

  let label: HTMLElement;
  let showTooltip: boolean;
  $: updateTooltip($isSidebarOpen);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function updateTooltip(_: boolean) {
    setTimeout(() => {
      showTooltip = label?.offsetHeight < label?.scrollHeight;
    }, 500);
  }

  function updateSelection(feature: Feature, selection?: Selection) {
    isSelected =
      ((selection?.lv1 && feature.code === selection?.lv1?.code) ||
        (selection?.lv2 && feature.code === selection?.lv2?.code)) ??
      false;
  }

  function updatA11yLabel(label: A11yLabels, feature: Feature) {
    if (feature.embedded) {
      ariaSuffix = label?.['external-link-suffix']
        ? ` (${label['external-link-suffix']})`
        : ' (link to a new window)';
    } else {
      ariaSuffix = '';
    }
  }
</script>

{#if !feature.disabled}
  <a
    aria-label="{feature.label}{ariaSuffix}"
    href={feature.href ?? 'javascript:void(null)'}
    target={feature.embedded}
    class="mc-sidebar-feature {isSelected ? 'is-selected' : ''} {$isSidebarOpen
      ? 'is-open'
      : 'is-close'}"
    id={feature.code}
  >
    <div class="mc-sidebar-feature__container">
      <span class="mc-sidebar-feature__label" bind:this={label}
        >{feature.label}</span
      >
      {#if showTooltip}
        <div aria-hidden="true" class="mc-sidebar-feature__label__tooltip">
          <div class="mc-sidebar-feature__label__tooltip__pointer"><div /></div>
          <span>{feature.label}</span>
        </div>
      {/if}
      {#if feature.embedded === '_blank'}
        <svg width="16px" height="16px" fill="white" viewBox="0 0 16 16"
          ><path
            fill-rule="evenodd"
            d="M9 2a1 1 0 000 2h1.586L6.293 8.293a1 1 0 001.414 1.414L12 5.414V7a1 1 0 102 0V3a1 1 0 00-1-1H9zM3 4a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1v-2a1 1 0 10-2 0v1H4V6h1a1 1 0 000-2H3z"
          /></svg
        >
      {/if}
    </div>
  </a>
{:else}
  <span
    aria-label={feature.label}
    aria-disabled="true"
    class="mc-sidebar-feature {isSelected ? 'is-selected' : ''} {$isSidebarOpen
      ? 'is-open'
      : 'is-close'} is-disabled"
    id={feature.code}
  >
    <div class="mc-sidebar-feature__container">
      <svg width="16px" height="16px" fill="#92a2a9" viewBox="0 0 16 16"
        ><g clip-path="url(#a)"
          ><path
            fill-rule="evenodd"
            d="M8 2a3 3 0 00-3 3v1H4a1 1 0 00-1 1v6a1 1 0 001 1h8a1 1 0 001-1V7a1 1 0 00-1-1h-1V5a3 3 0 00-3-3zm1 4V5a1 1 0 10-2 0v1h2zm-4 6V8h6v4H5z"
          /></g
        ><defs><clipPath id="a"><path d="M0 0h16v16H0z" /></clipPath></defs
        ></svg
      >
      <span>{feature.label}</span>
    </div>
  </span>
{/if}

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/_all-settings';
  @import './sidebar-variables';

  .mc-sidebar-feature {
    font-size: 16px;
    min-width: 200px;
    cursor: pointer;
    flex: 1;
    display: flex;
    text-decoration: none;
    color: white;
    line-height: 22px;
    overflow: hidden;

    &__container {
      display: flex;
      align-items: center;
      width: 100%;
      padding-top: 8px;
      padding-bottom: 8px;

      span {
        max-width: 200px;
        min-width: 200px;
      }
    }

    &__label {
      flex: 1;
      margin-right: 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;

      &__tooltip {
        opacity: 0;
        transition: 0s opacity;
        background-color: #5b737d;
        border: 1px solid white;
        border-radius: 4px;
        position: absolute;
        pointer-events: none;
        left: calc(100% - 16px);
        padding: 8px 16px;
        width: 188px;

        &__pointer {
          position: absolute;
          top: 50%;
          left: -8px;
          top: calc(50% - 6px);
          width: 0;
          height: 0;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-right: 8px solid white;

          & div {
            position: relative;
            top: -6px;
            left: 2px;
            width: 0;
            height: 0;
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            border-right: 8px solid #5b737d;
          }
        }
      }

      &:hover + .mc-sidebar-feature__label__tooltip {
        opacity: 1;
        transition-delay: 1s;
      }
    }

    &__icon {
      margin-right: 8px;
    }

    &.is-selected {
      background: $selected-Link;
    }

    &.is-close {
      @include transition(padding 300ms ease-in-out);
      padding: 0px 0px;

      .mc-sidebar-feature__container {
        @include transition(padding-left 300ms ease-in-out);
        padding-left: 20px;
        width: 24px;
      }
    }

    &.is-open {
      @include transition(padding 300ms ease-in-out);
      padding: 0px 32px;

      .mc-sidebar-feature__container {
        @include transition(padding-left 300ms ease-in-out);
        padding-left: 32px;
      }
    }

    &.is-disabled {
      color: #92a2a9;
      fill: #92a2a9;
    }

    &:hover {
      background: #405d68;
    }

    &:focus-visible {
      box-shadow: none;
      border: none;
      outline: none;

      .mc-sidebar-feature__container {
        box-shadow: none;
        border: none;
        border-radius: 6px;
        outline: 1px solid #0b96cc;
      }
    }
  }
</style>
